/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks/useArsData.ts (ARS - Route Selections)
 🗓️ Created: 2025-01-15
 🧩 Description:
    Custom hook for fetching and managing single ARS profile data.
    Handles loading states and data adaptation to form state.

────────────────────────────────────────────────────────────────*/

import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { fetchArsProfileById } from '../api/arsApi';
import { FormState, ArsProfile } from '../types';
import { getDefaultFormState, applySequentialPriorities, createClientId, createEmptyRoute } from '../utils/formDefaults';
import { useI18n } from '../../../hooks/useI18n';
import { useNotifications } from '../../../contexts/NotificationContext';

/**
 * Hook for fetching ARS profile data
 */
export function useArsData(
  id: string | undefined,
  duplicateId: string | null
) {
  const location = useLocation();
  const { t } = useI18n();
  const { showError } = useNotifications();
  const [formData, setFormData] = useState<FormState | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const lastLoadedIdRef = useRef<string | null>(null);
  const lastLocationKeyRef = useRef<string>('');

  useEffect(() => {
    const loadId = duplicateId || id;
    const savedDataKey = 'ars:saveAndNewData';
    const locationKey = location.key || location.pathname + location.search;

    if (!loadId || loadId === 'new') {
      // Check if we need to reset:
      // 1. ID changed
      // 2. Location key changed (React Router navigation)
      // 3. sessionStorage timestamp indicates reset needed
      const resetTimestamp = sessionStorage.getItem(savedDataKey + ':reset');
      const lastResetTimestamp = lastLoadedIdRef.current === 'new' ? 
        sessionStorage.getItem(savedDataKey + ':lastReset') : null;
      
      const shouldReset = lastLoadedIdRef.current !== 'new' || 
                          lastLocationKeyRef.current !== locationKey ||
                          (lastLoadedIdRef.current === 'new' && loadId === 'new' && 
                           resetTimestamp && resetTimestamp !== lastResetTimestamp);
      
      if (shouldReset) {
        // Always reset when navigating to new form (even if lastLoadedIdRef was 'new')
        // This ensures clean state after "Save and New"
        // Clear sessionStorage for "Save and New" functionality
        try {
          sessionStorage.removeItem(savedDataKey);
          if (resetTimestamp) {
            sessionStorage.setItem(savedDataKey + ':lastReset', resetTimestamp);
            sessionStorage.removeItem(savedDataKey + ':reset');
          }
        } catch (error) {
          // Ignore errors when clearing
        }
        
        // Reset form to default values
        if (!duplicateId) {
          const defaultState = getDefaultFormState();
          defaultState.routes = applySequentialPriorities([createEmptyRoute(1)]);
          setFormData(defaultState);
        } else {
          setFormData(null);
        }
        lastLoadedIdRef.current = 'new';
        lastLocationKeyRef.current = locationKey;
      }
      return;
    }

    if (lastLoadedIdRef.current === loadId && lastLocationKeyRef.current === locationKey) {
      return;
    }

    lastLoadedIdRef.current = loadId;
    lastLocationKeyRef.current = locationKey;
    setIsLoading(true);

    fetchArsProfileById(loadId)
      .then((profile: ArsProfile) => {
        const suffix = t('ars.actions.duplicate');
        const normalised: FormState = {
          name: duplicateId ? `${profile.name ?? ''} (${suffix})`.trim() : (profile.name ?? '').trim(),
          description: (profile.description ?? '').trim(),
          enabled: profile.enabled ?? true,
          routes: applySequentialPriorities(
            (profile.routes ?? []).map((route) => ({
              clientId: createClientId(),
              id: duplicateId ? undefined : route.id,
              outboundRouteId: route.outboundRouteId ?? route.outbound_route_id ?? null,
              timeGroupId: route.timeGroupId ?? route.time_group_id ?? null,
              priority: route.priority ?? 0,
              enabled: route.enabled ?? true
            }))
          )
        };

        setFormData(normalised);
      })
      .catch((error) => {
        const message = error instanceof Error ? error.message : t('ars.messages.loadError');
        showError(message);
        lastLoadedIdRef.current = null;
        setFormData(null);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [id, duplicateId, showError, t, location.key, location.pathname, location.search]);

  return {
    formData,
    isLoading
  };
}

